import { useEffect, useState } from 'react';

import { Spinner } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { STORAGE_KEYS } from '@constants/storage';
import { useLazyGetLeadPaymentOrderHistoryQuery } from '@features/lead-management/queries/LeadManagementQuery';
import { preDefinedKey } from '@features/transactions/constants';
import { getTransactionStatus } from '@features/transactions/helper';
import {
  getTransactionHistoryKeys,
  getTransactionHistoryTitle
} from '@features/transactions/helpers/transactionHistory';
import { useLazyGetPaymentOrderHistoryQuery } from '@features/transactions/queires/TransactionsQuery';
import { formatISODate } from '@utils/date';
import { getLocalStorageItem } from '@utils/storage';

import TransactionHistory from '../transaction-history/TransactionHistory';

interface PaymentHistoryProps {
  branchId: string;
  orderId: string;
  paymentStatus?: string;
  paymentSettlementStatus?: string;
  leadId?: string;
}

const PaymentHistory = ({
  branchId,
  orderId,
  paymentStatus = '',
  paymentSettlementStatus = '',
  leadId
}: PaymentHistoryProps) => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const [paymentHistories, setPaymentHistory] = useState<any>([]);

  const [
    getPaymentOrderHistory,
    {
      isLoading: paymentOrderHistoryLoading,
      isFetching: paymentHistoryFetching,
      isUninitialized: paymentHistoryUninitialized
    }
  ] = useLazyGetPaymentOrderHistoryQuery();

  const [
    getLeadPaymentOrderHistory,
    {
      isLoading: leadPaymentOrderHistoryLoading,
      isFetching: leadPaymentHistoryFetching,
      isUninitialized: leadPaymentHistoryUninitialized
    }
  ] = useLazyGetLeadPaymentOrderHistoryQuery();

  const handleGetPaymentOrderHistoryDetails = async () => {
    if (leadId) {
      const res = await getLeadPaymentOrderHistory({
        organizationId,
        leadId,
        branchId,
        paymentOrderId: orderId
      });
      if ('data' in res) {
        setPaymentHistory(res.data?.data || res.data);
      }
    } else {
      const res = await getPaymentOrderHistory({
        params: { organizationId, branchId, orderId }
      });
      if ('data' in res) {
        setPaymentHistory(res.data);
      }
    }
  };

  const paymentHistoryData = paymentHistories.map(
    (paymentHistory: any, historyIndex: number) => {
      const transactionHistoryKeys = getTransactionHistoryKeys(
        paymentHistory.payment_mode,
        paymentHistory.paid_amount,
        paymentHistory.type
      );

      const transactionHistoryData = transactionHistoryKeys?.map((key) => {
        const getTransactionHistoryValue = () => {
          if (key === preDefinedKey.paidAt) {
            return formatISODate({
              date: paymentHistory[key],
              format:
                paymentHistory.payment_mode === 'automatic' ||
                paymentHistory.type === 'discount'
                  ? 'DD MMM YYYY at hh:mm am/pm'
                  : 'DD MMM YYYY'
            });
          }
          if (
            key === preDefinedKey.markedPaidAt ||
            key === preDefinedKey.settledAt
          ) {
            return paymentHistory[key] !== 'null'
              ? formatISODate({
                  date: paymentHistory[key],
                  format: 'DD MMM YYYY at hh:mm am/pm'
                })
              : '--';
          }
          if (key === preDefinedKey.paymentMode) {
            return getTransactionHistoryTitle(
              key,
              paymentHistory.payment_mode,
              paymentHistory.payment_method,
              paymentHistory.type
            );
          }

          return paymentHistory[key];
        };

        const getPaymentModeClassName = () => {
          if (paymentHistory.type === 'discount') {
            return 'bg-cofeeTeal50 !text-cofeeTeal300';
          }
          if (paymentHistory.settlement_status === 'settled') {
            return 'bg-primary10 !text-primary40 ';
          }
          return 'bg-blue !text-blue1';
        };

        const getPaymentModeTitle = () => {
          if (key === preDefinedKey.paymentMode) {
            if (paymentHistory.type === 'discount') {
              return '';
            }
            return 'Paid';
          }
          return getTransactionHistoryTitle(
            key,
            paymentHistory.payment_mode,
            paymentHistory.payment_method,
            paymentHistory.type
          );
        };

        return {
          title: getPaymentModeTitle(),
          value: getTransactionHistoryValue(),
          className:
            key === preDefinedKey.paymentMode
              ? `px-2.5 py-1 rounded-lg ${getPaymentModeClassName()}`
              : ''
        };
      });

      const paymentModeIndex = transactionHistoryData.findIndex(
        (item: any) => item.className !== ''
      );

      if (paymentModeIndex !== -1) {
        const amountRow = {
          title: 'Amount',
          value: (
            <div className="flex items-center justify-center">
              <CurrencyAmount
                value={Number(paymentHistory.paid_amount)}
                className="gap-0.5"
                symbolClass=""
                valueClass=""
              />
            </div>
          ),
          className: ''
        };

        transactionHistoryData.splice(paymentModeIndex + 1, 0, amountRow);
      }

      return {
        data: transactionHistoryData,
        key: `${paymentHistory.paid_at || ''}-${
          paymentHistory.paid_amount || ''
        }-${historyIndex}`
      };
    }
  );

  const isLoadingPaymentHistory = leadId
    ? leadPaymentOrderHistoryLoading ||
      leadPaymentHistoryFetching ||
      leadPaymentHistoryUninitialized
    : paymentOrderHistoryLoading ||
      paymentHistoryFetching ||
      paymentHistoryUninitialized;

  useEffect(() => {
    handleGetPaymentOrderHistoryDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <p className="pb-3 pt-6 text-[16px] font-medium text-content">
        Past Transactions
      </p>
      {isLoadingPaymentHistory ? (
        <Spinner />
      ) : (
        <>
          {paymentHistoryData.length > 0 && (
            <div className="w-full rounded-xl border border-grey40 px-4">
              {paymentHistoryData.map((paymentHistoryItem: any) => (
                <TransactionHistory
                  key={paymentHistoryItem.key}
                  isLoading={paymentOrderHistoryLoading}
                  paymentHistoryVal={paymentHistoryItem.data}
                  transactionStatus={getTransactionStatus(
                    paymentStatus,
                    paymentSettlementStatus
                  )}
                />
              ))}
            </div>
          )}
          {paymentHistoryData.length === 0 && (
            <div className="text-center text-sm font-medium text-grey">
              No transactions to show
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PaymentHistory;
